import React, { useState } from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { ActivityFeed } from '../../components/portal/ActivityFeed';
import { NeedsAttention } from '../../components/portal/NeedsAttention';
import { projectStatus } from '../../utils/status';
import { BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from '../../components/portal/adminStyles';
interface ProjectsProps {
  /** The New Project form is opened from the header. */
  creating: boolean;
  setCreating: (open: boolean) => void;
}
export function Projects({
  creating,
  setCreating
}: ProjectsProps) {
  const {
    activeProjects,
    archivedProjects,
    templates,
    openProject,
    createProject,
    reopenProject,
    notify
  } = usePortal();
  const [client, setClient] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [templateId, setTemplateId] = useState('');
  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!client.trim() || !name.trim()) return;
    createProject({
      client: client.trim(),
      name: name.trim(),
      type: type.trim(),
      templateId
    });
    notify(`${client.trim()} project created`);
    setClient('');
    setName('');
    setType('');
    setTemplateId('');
    setCreating(false);
  }
  return <div data-id="element-777" data-mp="30nmucmq1xq9w">
      {creating && <form onSubmit={submit} className="mb-12 border border-paper/25 px-6 py-8 sm:px-8" data-id="element-778" data-mp="30oo50q35oar3">
          <h2 className="font-display text-2xl leading-none" data-id="element-779" data-mp="2cytrs7exck6b">New Project</h2>
          <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2" data-id="element-780" data-mp="280a524sib4cw">
            <label className="block" data-id="element-781" data-mp="2lqdpcq7tsnzp">
              <span className="label block text-paper/40" data-id="element-782" data-mp="2lrfajnik2qv5">Client</span>
              <input value={client} onChange={event => setClient(event.target.value)} placeholder="e.g. Example Group" autoFocus className={FIELD} data-id="element-783" data-mp="2gzdtzpecudzb" />
            </label>
            <label className="block" data-id="element-784" data-mp="1p6wt7i0hjon5">
              <span className="label block text-paper/40" data-id="element-785" data-mp="3oe9axq0pq53f">Project Name</span>
              <input value={name} onChange={event => setName(event.target.value)} placeholder="e.g. Leadership Series" className={FIELD} data-id="element-786" data-mp="3tcau0ay524ew" />
            </label>
            <label className="block" data-id="element-787" data-mp="6voh15ty3ns3">
              <span className="label block text-paper/40" data-id="element-788" data-mp="6yu6wnaeetov">Project Type</span>
              <input value={type} onChange={event => setType(event.target.value)} placeholder="e.g. Video Series" className={FIELD} data-id="element-789" data-mp="35g6kr7wfi241" />
            </label>
            <label className="block" data-id="element-790" data-mp="3kbw6d4xpcd88">
              <span className="label block text-paper/40" data-id="element-791" data-mp="3kcxi7bmjl3r0">Start From (optional)</span>
              <select value={templateId} onChange={event => setTemplateId(event.target.value)} className={SELECT} data-id="element-792" data-mp="undet1pjd4gf">
                <option value="" className={OPTION} data-id="element-793" data-mp="2k3g16kh4m31m">
                  Blank project
                </option>
                {templates.map(template => <option key={template.id} value={template.id} className={OPTION} data-id="element-794" data-mp="2f5ehbxbvlzp3">
                    {template.name}
                  </option>)}
              </select>
            </label>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-6" data-id="element-795" data-mp="25cyykxsr9735">
            <button type="submit" className={BUTTON_SOLID} data-id="element-796" data-mp="20h9vikhj1823">
              Create Project
            </button>
            <button type="button" onClick={() => setCreating(false)} className={BUTTON_QUIET} data-id="element-797" data-mp="1wctt4t74girm">
              Cancel
            </button>
          </div>
          <p className="label-sm mt-8 max-w-lg leading-relaxed text-paper/30" data-id="element-798" data-mp="1mgh2y5ascn1m">
            Templates are starting structures only — every deliverable can be added, renamed,
            reordered or removed afterwards.
          </p>
        </form>}

      <NeedsAttention data-id="element-799" data-mp="3grvqcfgbqae7" />

      <div className="mt-16 grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16" data-id="element-800" data-mp="2egkbiyfrp1h1">
        <section aria-labelledby="active-projects" className="lg:col-span-8" data-id="element-801" data-mp="2eeprdmcblde0">
          <h2 id="active-projects" className="label border-b border-ruleDark pb-4 text-paper/40" data-id="element-802" data-mp="2ihcoo2rrfqil">
            Active Projects
          </h2>
          <ul className="m-0 list-none p-0" data-id="element-803" data-mp="29geet3un4os3">
            {activeProjects.map(row => {
            const waiting = row.deliverables.filter(item => item.status === 'Ready for Review' && item.visibleToClient).length;
            const changes = row.deliverables.reduce((total, item) => total + item.changeRequests.filter(request => !request.resolved).length, 0);
            return <li key={row.id} className="border-b border-ruleDark" data-id="element-804" data-mp="1buqmov57wsmd">
                  <button type="button" onClick={() => openProject(row.id)} className="group grid w-full grid-cols-1 items-baseline gap-x-8 gap-y-3 py-8 text-left sm:grid-cols-12" data-id="element-805" data-mp="1gsiku6wghaz0">
                    <span className="font-display text-3xl leading-none sm:col-span-5 sm:text-4xl" data-id="element-806" data-mp="t7hqtry7vfuw">
                      {row.client}
                    </span>
                    <span className="font-display text-xl text-paper/70 sm:col-span-4" data-id="element-807" data-mp="1251t947tfi73">
                      {row.name}
                    </span>
                    <span className="label text-paper/40 sm:col-span-1" data-id="element-808" data-mp="1g3hyeqq150n4">
                      {row.clientUsers.length} user{row.clientUsers.length === 1 ? '' : 's'}
                    </span>
                    <span className="label flex items-center justify-between gap-4 text-paper sm:col-span-2 sm:justify-end" data-id="element-809" data-mp="1pxs1b2cllhc3">
                      {changes > 0 ? 'Changes' : waiting > 0 ? 'With client' : projectStatus(row)}
                      <ArrowRightIcon className="h-3.5 w-3.5 shrink-0 opacity-40 transition-transform duration-150 ease-out group-hover:translate-x-1 group-hover:opacity-100" strokeWidth={1.25} data-id="element-810" data-mp="1uv0mzcwyg3i3" />
                    </span>
                  </button>
                </li>;
          })}
            {activeProjects.length === 0 && <li className="border-b border-ruleDark py-8 font-display text-xl text-paper/40" data-id="element-811" data-mp="2gnd5drus0dze">
                No active projects.
              </li>}
          </ul>

          {archivedProjects.length > 0 && <>
              <h2 className="label mt-16 border-b border-ruleDark pb-4 text-paper/40" data-id="element-812" data-mp="2plq4fc9mdpg0">
                Archived Projects
              </h2>
              <ul className="m-0 list-none p-0" data-id="element-813" data-mp="2zlh0hidtwci7">
                {archivedProjects.map(row => <li key={row.id} className="flex flex-wrap items-baseline justify-between gap-x-8 gap-y-3 border-b border-ruleDark py-6" data-id="element-814" data-mp="108uzutndoxrk">
                    <button type="button" onClick={() => openProject(row.id)} className="min-w-0 flex-1 text-left transition-opacity duration-150 ease-out hover:opacity-70" data-id="element-815" data-mp="xnx2nhsn3lnw">
                      <span className="block font-display text-2xl leading-none text-paper/70" data-id="element-816" data-mp="nyn8lwe8jsx5">
                        {row.client}
                      </span>
                      <span className="label-sm mt-3 block text-paper/35" data-id="element-817" data-mp="1glzwc9w4u6s1">
                        {row.name} · Archived · Updated {row.updated}
                      </span>
                    </button>
                    <button type="button" onClick={() => {
                reopenProject(row.id);
                notify(`${row.client} reopened`);
              }} className={BUTTON_QUIET} data-id="element-818" data-mp="16ntvzwpz58pa">
                      Reopen
                    </button>
                  </li>)}
              </ul>
              <p className="label-sm mt-6 leading-relaxed text-paper/30" data-id="element-819" data-mp="fx63adunu1vk">
                Nothing is deleted — comments, version history, deliverables and Drive links are
                preserved.
              </p>
            </>}
        </section>

        <div className="lg:col-span-4 lg:border-l lg:border-ruleDark lg:pl-12" data-id="element-820" data-mp="az4kzv5266l5">
          <ActivityFeed data-id="element-821" data-mp="yp8nfz6ig0ak" />
        </div>
      </div>
    </div>;
}